import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import { Link } from 'foundations-components/transitional/components/Link';
import UISection from 'UIComponents/section/UISection';
import { AboutOption } from 'automation-ui-canvas/components/flow-header/menu-bar/menuOptions/fileOptions';
import { useGetNameAndDescription } from 'automation-ui-management/components/WorkflowNameWithDescription/nameAndDescriptionHooks';
import { useCallback, useState, useEffect } from 'react';
import { ReviewMetadataColumn, ReviewMetadataRow, StyledMetadataValue } from 'automation-ui-canvas/containers/review/StyledMetadataDetails';
import TeamPermissionsMetadata from 'automation-ui-canvas/containers/review/TeamPermissionsMetadata';
import CampaignMetadata from 'automation-ui-canvas/containers/review/CampaignMetadata';
import { getIsClassicWorkflow } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import UpdateMetadata from 'automation-ui-canvas/containers/review/UpdateMetadata';
import CreateMetadata from 'automation-ui-canvas/containers/review/CreateMetadata';
import BusinessUnitMetadata from 'automation-ui-canvas/containers/review/BusinessUnitMetadata';
import FolderMetadata from 'automation-ui-canvas/containers/review/FolderMetadata';
import AiDescriptionSuggestion from 'automation-ui-management/components/WorkflowNameWithDescription/AiDescriptionSuggestion';
import AiNameSuggestion from 'automation-ui-management/components/WorkflowNameWithDescription/AiNameSuggestion';
import useOpenMenu from 'automation-ui-canvas/components/flow-header/menu-bar/useOpenMenu';
import { ReviewOptionId } from 'automation-ui-canvas/components/flow-header/menu-bar/menuOptions/constants/MenuOptionIds';
import { useIsUngated } from 'automation-ui-core/hooks/authHooks';
import { AI_NAME_SUGGESTION } from 'automation-ui-canvas/constants/Gates';
import { checkIfDefaultName } from 'automation-ui-canvas/create/utils/checkIfDefaultName';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DetailsStep = ({
  flow,
  modalState,
  setModalState
}) => {
  const {
    flow: flowWithCache,
    description
  } = useGetNameAndDescription({
    flowId: flow.flowId,
    flowFromProps: flow.toHybridFlowInterface()
  });
  const isUngatedForAiNameSuggestion = useIsUngated(AI_NAME_SUGGESTION);
  const openMenu = useOpenMenu();
  const handleOpenPanelWithRedirect = useCallback(panelId => {
    openMenu({
      menuOptionId: panelId,
      redirectMenuOptionId: ReviewOptionId
    });
  }, [openMenu]);
  const [localModalState, setLocalModalState] = useState({
    aiFlowName: modalState.get('aiFlowName'),
    aiFlowDescription: modalState.get('aiFlowDescription')
  });
  const setGeneratedDescription = newDescription => {
    setLocalModalState(Object.assign({}, localModalState, {
      aiFlowDescription: newDescription
    }));
  };
  const setGeneratedName = newName => {
    setLocalModalState(Object.assign({}, localModalState, {
      aiFlowName: newName
    }));
  };
  useEffect(() => {
    setModalState(prev => {
      let updated = prev;
      if (localModalState.aiFlowName !== undefined) {
        updated = updated.set('aiFlowName', localModalState.aiFlowName);
      }
      if (localModalState.aiFlowDescription !== undefined) {
        updated = updated.set('aiFlowDescription', localModalState.aiFlowDescription);
      }
      return updated;
    });
  }, [localModalState, setModalState]);
  const descriptionInfo = /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(StyledMetadataValue, {
      children: description
    }), /*#__PURE__*/_jsx(View, {
      display: "block",
      children: /*#__PURE__*/_jsx(Link, {
        onClick: () => {
          handleOpenPanelWithRedirect(AboutOption.id);
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "platformReview.v2.detailsStep.editDescription"
        })
      })
    })]
  });
  const nameInfo = /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(StyledMetadataValue, {
      children: (flowWithCache === null || flowWithCache === void 0 ? void 0 : flowWithCache.name) || flow.name
    }), /*#__PURE__*/_jsx(View, {
      display: "block",
      children: /*#__PURE__*/_jsx(Link, {
        onClick: () => {
          handleOpenPanelWithRedirect(AboutOption.id);
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "platformReview.v2.detailsStep.editName"
        })
      })
    })]
  });
  return /*#__PURE__*/_jsxs(UISection, {
    "data-test-id": "review-details-step",
    children: [/*#__PURE__*/_jsx(ReviewMetadataRow, {
      children: /*#__PURE__*/_jsxs(ReviewMetadataColumn, {
        children: [/*#__PURE__*/_jsx(UIFormLabel, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "platformReview.v2.detailsStep.name"
          })
        }), isUngatedForAiNameSuggestion && checkIfDefaultName(flow) ? /*#__PURE__*/_jsx(_Fragment, {
          children: /*#__PURE__*/_jsx(AiNameSuggestion, {
            onEdit: newName => openMenu({
              menuOptionId: AboutOption.id,
              redirectMenuOptionId: ReviewOptionId,
              initialState: {
                name: newName
              }
            }),
            flowId: flow.flowId,
            version: flow.version,
            fallbackElement: nameInfo,
            setAiFlowName: setGeneratedName,
            aiFlowName: localModalState.aiFlowName
          })
        }) : nameInfo]
      })
    }), /*#__PURE__*/_jsx(ReviewMetadataRow, {
      children: /*#__PURE__*/_jsxs(ReviewMetadataColumn, {
        children: [/*#__PURE__*/_jsx(UIFormLabel, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "platformReview.v2.detailsStep.description"
          })
        }), description ? descriptionInfo : /*#__PURE__*/_jsx(_Fragment, {
          children: /*#__PURE__*/_jsx(AiDescriptionSuggestion, {
            onEdit: newDescription => openMenu({
              menuOptionId: AboutOption.id,
              redirectMenuOptionId: ReviewOptionId,
              initialState: {
                description: newDescription
              }
            }),
            flowId: flow.flowId,
            fallbackElement: descriptionInfo,
            setAiFlowDescription: setGeneratedDescription,
            aiFlowDescription: localModalState.aiFlowDescription
          })
        })]
      })
    }), /*#__PURE__*/_jsxs(ReviewMetadataRow, {
      children: [/*#__PURE__*/_jsx(TeamPermissionsMetadata, {
        flow: flow,
        handleOpenPanelWithRedirect: handleOpenPanelWithRedirect
      }), getIsClassicWorkflow(flow) && /*#__PURE__*/_jsx(CampaignMetadata, {
        flow: flow,
        handleOpenPanelWithRedirect: handleOpenPanelWithRedirect
      })]
    }), /*#__PURE__*/_jsxs(ReviewMetadataRow, {
      children: [/*#__PURE__*/_jsx(BusinessUnitMetadata, {
        flow: flow,
        handleOpenPanelWithRedirect: handleOpenPanelWithRedirect
      }), /*#__PURE__*/_jsx(FolderMetadata, {
        flow: flow,
        handleOpenPanelWithRedirect: handleOpenPanelWithRedirect
      })]
    }), /*#__PURE__*/_jsx(UpdateMetadata, {
      flow: flow
    }), /*#__PURE__*/_jsx(CreateMetadata, {
      flow: flow
    })]
  });
};
export default DetailsStep;